// Shared "orange" used by the projected-balance stats when the balance dips into
// the $0–$1000 cushion zone. The palettes only carry green (credit) + red
// (warn/hot), so the mid-tier warning color lives here. Resolves to a CSS var so
// it tracks light/dark (see index.css + ThemeContext).
export const BALANCE_CUSHION_ORANGE = 'var(--gf-cushion)';

// Color a projected balance (e.g. "lowest point", "in 30 days") by severity:
//   > $1000  → green  (comfortable)
//   $0–$1000 → orange (cushion getting thin)
//   < $0     → red    (overdrawn)
export function balanceTone(value: number, green: string, red: string): string {
  if (value > 1000) return green;
  if (value < 0) return red;
  return BALANCE_CUSHION_ORANGE;
}

export interface StreamPalette {
  bg: string;
  surface: string;
  ink: string;
  mute: string;
  line: string;
  accent: string;
  credit: string;
  debit: string;
  warn: string;
  warnBg: string;
}

export interface PoolPalette {
  bg: string;
  surface: string;
  ink: string;
  mute: string;
  accent: string;
  accent2: string;
  credit: string;
  debit: string;
  hot: string;
  cool: string;
  line: string;
}

export const streamPalettes: Record<string, StreamPalette> = {
  // Default palette — every shipped surface uses Lagoon, so its values resolve
  // to CSS vars (defined in index.css) and swap automatically with the theme.
  // The other variants below are experimental/unused and stay literal (light).
  Lagoon: {
    bg: 'var(--gf-bg)', surface: 'var(--gf-surface)', ink: 'var(--gf-ink)', mute: 'var(--gf-mute)',
    line: 'var(--gf-line)', accent: 'var(--gf-accent)', credit: 'var(--gf-credit)', debit: 'var(--gf-debit)',
    warn: 'var(--gf-warn)', warnBg: 'var(--gf-warn-bg)',
  },
  Indigo: {
    bg: '#f4f2ec', surface: '#ffffff', ink: '#0c1116', mute: '#5d6671',
    line: '#e6e2d8', accent: '#1f3bd0', credit: '#127a55', debit: '#384049',
    warn: '#b53a3a', warnBg: '#f6e0dd',
  },
  Forest: {
    bg: '#f3f3ee', surface: '#ffffff', ink: '#10160f', mute: '#5b6359',
    line: '#e5e4d9', accent: '#22624a', credit: '#22624a', debit: '#384049',
    warn: '#a14638', warnBg: '#f3e1dc',
  },
  Sunset: {
    bg: '#fbf5ec', surface: '#ffffff', ink: '#1a1310', mute: '#6a5e54',
    line: '#ece2d2', accent: '#c43d18', credit: '#117153', debit: '#3c322a',
    warn: '#a83a26', warnBg: '#f6dfd5',
  },
};

export const poolPalettes: Record<string, PoolPalette> = {
  // Default calendar palette — varuized like streamPalettes.Lagoon above.
  Lagoon: {
    bg: 'var(--gf-pool-bg)', surface: 'var(--gf-pool-surface)', ink: 'var(--gf-pool-ink)', mute: 'var(--gf-pool-mute)',
    accent: 'var(--gf-pool-accent)', accent2: 'var(--gf-pool-accent2)', credit: 'var(--gf-pool-credit)', debit: 'var(--gf-pool-debit)',
    hot: 'var(--gf-pool-hot)', cool: 'var(--gf-pool-cool)', line: 'var(--gf-pool-line)',
  },
  Grape: {
    bg: '#f7f1e7', surface: '#fffaf2', ink: '#1d1322', mute: '#7a6e7e',
    accent: '#7c3aed', accent2: '#d4ed3a', credit: '#1a9b6c', debit: '#e85d75',
    hot: '#ff5a5f', cool: '#c8b7ff', line: 'rgba(29,19,34,0.08)',
  },
  Reef: {
    bg: '#eef6f4', surface: '#ffffff', ink: '#0d1f1c', mute: '#5e7975',
    accent: '#06b6d4', accent2: '#fcd34d', credit: '#0fbf7e', debit: '#f97373',
    hot: '#ef4444', cool: '#a7eaee', line: 'rgba(13,31,28,0.08)',
  },
  Tang: {
    bg: '#fef3e6', surface: '#fff8ee', ink: '#2a1a0f', mute: '#83695b',
    accent: '#f97316', accent2: '#a3e635', credit: '#16a34a', debit: '#e11d48',
    hot: '#ef4444', cool: '#fed7aa', line: 'rgba(42,26,15,0.08)',
  },
};

// The `* Fallback` families are metric-matched faces (web/src/fonts-fallback.css)
// that occupy the same box as the real web font, so the async font swap causes
// ~no layout shift. They sit between the web font and the generic family.
export const fonts = {
  sans: "'DM Sans', 'DM Sans Fallback', system-ui, sans-serif",
  display:
    "'Bricolage Grotesque', 'Bricolage Grotesque Fallback', 'DM Sans', 'DM Sans Fallback', system-ui, sans-serif",
  brand: "'Instrument Serif', 'Instrument Serif Fallback', Georgia, serif",
  mono: "'JetBrains Mono', 'JetBrains Mono Fallback', ui-monospace, monospace",
  poolMono: "'Space Mono', 'Space Mono Fallback', ui-monospace, monospace",
};

export const monoStyle: React.CSSProperties = {
  fontFamily: fonts.mono,
  fontFeatureSettings: '"tnum"',
};

export const poolMonoStyle: React.CSSProperties = {
  fontFamily: fonts.poolMono,
  fontFeatureSettings: '"tnum"',
};

// Motion tokens — from `Motion study.html`. Three easings + four durations
// cover every motion in the system; don't invent new ones.
export const ease = {
  gel: 'cubic-bezier(.22,1,.36,1)',
  ooze: 'cubic-bezier(.6,.02,.74,.04)',
  pulse: 'cubic-bezier(.45,0,.55,1)',
} as const;

export const duration = {
  quick: '300ms',
  loading: '1.2s',
  reveal: '1.6s',
  idle: '3.6s',
} as const;

// Brand color hex constants used outside palette context (favicon, theme-color,
// transactional emails, marketing surfaces).
export const brand = {
  lagoon: '#0A6E7E',
  lagoonMid: '#3D9099',
  lagoonAqua: '#BFE1E3',
  lagoonDeep: '#063E48',
  paper: '#f4f2ec',
  ink: '#0c1116',
} as const;

export type StreamPaletteName = keyof typeof streamPalettes;
export type PoolPaletteName = keyof typeof poolPalettes;

export const defaultStreamPalette: StreamPaletteName = 'Lagoon';
export const defaultPoolPalette: PoolPaletteName = 'Lagoon';

export const categoryColors: Record<string, string> = {
  income: '#16855f', housing: '#7c4dff', insurance: '#0ea5e9', utilities: '#0891b2',
  loans: '#a84a7a', subs: '#b25c2e', household: '#7a8a3a', credit: '#a23838',
  taxes: '#3d4651', travel: '#1f7a8c', fun: '#c2410c', cash: '#5a6470',
  gifts: '#a847a1', food: '#b75c25', groceries: '#3b8a3b',
};
